/**
 * The hint under Easify's "Choose a Chain" picker (#131).
 *
 * Easify mounts its picker (`.tpo_option-set-wrapper`) into the product form after load, just
 * before the buy buttons - so after this hint, which buy-buttons.liquid renders under the picker's
 * placeholder. Once it mounts, the hint moves itself below the picker, back where it sat under the
 * placeholder.
 *
 * Easify renders each option as a `.tpo_option-type-selection` button, and its admin has no way to
 * mark one, so this also tags the option whose label matches `data-popular-option` - again
 * whenever Easify re-renders the list.
 */

const PICKER_SELECTOR = '.tpo_option-set-wrapper.tpo_has-option-set';
const OPTION_SELECTOR = '.tpo_option-type-selection';
const LABEL_SELECTOR = '.tpo_option_type_label';
const LABEL_WRAPPER_SELECTOR = '.tpo_option_type_label_wrapper';
const TAG_CLASS = 'chain-picker-hint__tag';

class ChainPickerHint extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    const form = this.closest('form');
    if (!form) return;

    const update = () => {
      this.#tagPopularOption(form);
      this.#followPicker(form);
    };
    update();
    this.#observer = new MutationObserver(update);
    this.#observer.observe(form, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * Moving the element disconnects and reconnects it, which re-runs connectedCallback - by then
   * it's already after the picker, so it doesn't move again.
   * @param {HTMLFormElement} form
   */
  #followPicker(form) {
    const picker = form.querySelector(PICKER_SELECTOR);
    if (picker && picker.nextElementSibling !== this) picker.after(this);
  }

  /** @param {HTMLFormElement} form */
  #tagPopularOption(form) {
    const { popularOption, popularTag } = this.dataset;
    if (!popularOption || !popularTag) return;

    for (const option of form.querySelectorAll(OPTION_SELECTOR)) {
      if (option.querySelector(LABEL_SELECTOR)?.textContent?.trim() !== popularOption) continue;
      if (option.querySelector(`.${TAG_CLASS}`)) continue;

      // After the price, so on narrow screens the tag wraps rather than the price.
      const tag = document.createElement('span');
      tag.className = TAG_CLASS;
      tag.textContent = popularTag;
      (option.querySelector(LABEL_WRAPPER_SELECTOR) ?? option).append(tag);
    }
  }
}

if (!customElements.get('chain-picker-hint')) {
  customElements.define('chain-picker-hint', ChainPickerHint);
}
